import { whirlpoolSteps } from "@bl/domain";
import type { BrewDetail, RecipeDetail, RecipeLine } from "./api";
import { lineName, showBarrels, showGallons, showGravity } from "./recipeFormat";

/**
 * A recipe's printed Brew Log (Scott's Jam, 2026-10-08: print a recipe with "basics from the brew log
 * ... without the internet or a device", to hang on the tank). It is White Oak's own paper sheet, the
 * blank Tom gave us (`interviews/artifacts/2026-09-04-tom-brewlog-blank.pdf`), in its order and with its
 * names, filled in from the recipe and, printed from a brew, from what the brew has recorded. Changed
 * from the paper (Tyler, 2026-10-19): one turn, not two; none of the five fields nobody logs (Depth,
 * 90' Gravity, 90' pH, PolyClar, Aeration); a line for each boil addition in place of 1st, 2nd and 3rd
 * Hops; hops in pounds; and a blank for every reading brew day asks for, to type back from.
 */

/** A blank on the sheet: its name, what the brew recorded for it, and the recipe's figure beside it. */
export interface SheetField {
  label: string;
  value: string;
  /** The recipe's figure, printed small beside the blank; empty for none. */
  aim: string;
}

export interface BrewLog {
  name: string;
  /** Its style, version, batch and brew system, under the name. */
  about: string;
  brewDate: string;
  brewNumber: string;
  tank: string;
  targetOg: string;
  targetFg: string;
  /** The day's blanks in three runs: mash, boil, knockout. */
  fields: SheetField[][];
  additives: { name: string; amount: string }[];
  grist: { name: string; pounds: string }[];
  hops: { name: string; pounds: string; time: string }[];
  yeast: { name: string; gen: string }[];
  carbonation: string;
  notes: string;
}

const LITERS_PER_GALLON = 3.785411784;
const GRAMS_PER_POUND = 453.59237;

const trim = (value: number, places: number) =>
  value.toLocaleString("en-US", { maximumFractionDigits: places, minimumFractionDigits: 0 });
const gallons = (liters: string | number) => `${trim(Number(liters) / LITERS_PER_GALLON, 1)} gal`;
const fahrenheit = (celsius: string | number) => trim((Number(celsius) * 9) / 5 + 32, 1);
const recorded = (v: string | null, show: (v: string) => string) => (v === null ? "" : show(v));

/** Pounds, as the sheet's "#'s" columns take them, to two places. */
export const pounds = (grams: string | null) =>
  grams === null ? "" : trim(Number(grams) / GRAMS_PER_POUND, 2);

/** A day as written on the paper: 10/19/2026. */
export const sheetDate = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });

/** What the brewer pulls: the item it is linked to, as brew day names it, else the recipe's name. */
const nameOf = lineName;

/** When each kind of hop goes in, in the day's order. */
const HOP_ORDER: Record<string, number> = {
  mash: 0,
  first_wort: 1,
  boil: 2,
  whirlpool: 3,
  dry_hop: 4,
};

const minutes = (l: RecipeLine) => Number(l.minutes ?? 0);
const field = (label: string, value = "", aim = ""): SheetField => ({ label, value, aim });

export function brewLogOf(r: RecipeDetail, b: BrewDetail | null): BrewLog {
  const said = b?.readings;
  const hops = r.lines.filter((l) => l.kind === "hop");
  const steps = whirlpoolSteps(
    hops
      .filter((l) => l.hopUse === "whirlpool")
      .map((l) => ({
        id: l.id,
        tempC: l.tempC === null ? null : Number(l.tempC),
        minutes: minutes(l),
      })),
  );
  const stepOf = new Map(steps.flatMap((step, i) => step.map((l) => [l.id, i] as const)));
  const sorted = [...hops].sort(
    (a, z) =>
      (HOP_ORDER[a.hopUse ?? ""] ?? 5) - (HOP_ORDER[z.hopUse ?? ""] ?? 5) ||
      (a.hopUse === "whirlpool" ? (stepOf.get(a.id) ?? 0) - (stepOf.get(z.id) ?? 0) : 0) ||
      (a.hopUse === "boil" ? minutes(z) - minutes(a) : 0),
  );

  // Brew day's blanks, in the paper's order, with the readings brew day takes where the paper has none.
  const mashHops = hops.some((l) => l.hopUse === "mash");
  const firstWort = hops.some((l) => l.hopUse === "first_wort");
  const mash = [
    field("Strike °F", recorded(said?.strikeTempC ?? null, fahrenheit)),
    field("Mash In"),
    field(
      "Temp °F",
      recorded(said?.mashTempC ?? null, fahrenheit),
      r.mashSteps[0] ? `${fahrenheit(r.mashSteps[0].tempC)}°F` : "",
    ),
    ...(mashHops ? [field("Mash Hops")] : []),
    field(
      "Mash pH",
      recorded(said?.mashPh ?? null, (v) => trim(Number(v), 2)),
    ),
    field("Vorlauf"),
    field("Sparge Starts"),
    ...(firstWort ? [field("First Wort Hops")] : []),
    field("pH into Kettle"),
    field(
      "Fill Volume",
      recorded(said?.preBoilLiters ?? null, gallons),
      gallons(r.volumes.preBoilLiters),
    ),
    field(
      "Pre-boil Gravity",
      recorded(said?.preBoilGravity ?? null, (v) => showGravity(Number(v))),
      showGravity(r.preBoilGravity),
    ),
  ];

  // A line for the hops at each time, longest first, then the boil's other additions.
  const boilTimes = [...new Set(hops.filter((l) => l.hopUse === "boil").map(minutes))].sort(
    (a, z) => z - a,
  );
  const boilOthers = r.lines.filter((l) => l.kind === "other" && /boil/i.test(l.use ?? ""));
  const typedTemp = (step: readonly { id: string }[]) =>
    said?.whirlpoolTemps.find((w) => step.some((l) => l.id === w.recipeLineId))?.tempC ?? null;
  const boil = [
    field("Boil", "", `${Number(r.settings.boilMinutes)} min`),
    ...boilTimes.map((m) => field(`${m}' Hops`)),
    ...boilOthers.map((l) => field(nameOf(l))),
    field("Boil End"),
    ...steps.map((step, i) =>
      field(
        steps.length === 1 ? "WRL °F" : `WRL ${i + 1} °F`,
        recorded(typedTemp(step), fahrenheit),
        step[0]?.tempC == null ? "" : `${fahrenheit(step[0].tempC)}°F`,
      ),
    ),
    field("WRL End"),
  ];

  const knockout = [
    field("KO Temp"),
    field("KO pH"),
    field(
      "KO Gravity",
      recorded(said?.og ?? null, (v) => showGravity(Number(v))),
      showGravity(r.estimates.og),
    ),
    field(
      "Into Fermenter",
      recorded(said?.fermenterLiters ?? null, gallons),
      gallons(r.settings.batchLiters),
    ),
  ];

  const hopTime = (l: RecipeLine) => {
    const m = minutes(l);
    switch (l.hopUse) {
      case "mash":
        return "Mash";
      case "first_wort":
        return "First wort";
      case "whirlpool": {
        const step = steps.length > 1 ? ` ${(stepOf.get(l.id) ?? 0) + 1}` : "";
        return m ? `Whirlpool${step}, ${m} min` : `Whirlpool${step}`;
      }
      case "dry_hop": {
        const days = Number(l.days ?? 0);
        return days ? `Dry hop, ${days} ${days === 1 ? "day" : "days"}` : "Dry hop";
      }
      default:
        return `${m} min`;
    }
  };

  // The generation of harvested yeast it pitches, where brew day chose a harvest.
  const genOf = (l: RecipeLine) =>
    [
      ...new Set(
        (b?.lines.find((x) => x.recipeLineId === l.id)?.pulls ?? [])
          .map((p) => p.generation)
          .filter((g): g is number => g !== null),
      ),
    ].join(", ");

  // The tanks it is in or booked for, in order; none emptied, as the sheet hangs on where it is.
  const tanks = (b?.stays ?? []).filter((s) => s.state !== "emptied").map((s) => s.tank.name);

  const style = r.style ? `${r.style.name}${r.style.code ? ` ${r.style.code}` : ""}` : null;
  return {
    name: r.name,
    about: [
      style,
      `Version ${r.version}`,
      `${showGallons(r.batchLiters)} (${showBarrels(r.batchLiters)})`,
      r.brewSystem?.name ?? null,
    ]
      .filter(Boolean)
      .join(" · "),
    brewDate: b ? sheetDate(b.brewedOn) : "",
    brewNumber: b?.number == null ? "" : String(b.number),
    tank: tanks.join(", then "),
    targetOg: showGravity(r.estimates.og),
    targetFg: r.estimates.fg === null ? "" : showGravity(r.estimates.fg),
    fields: [mash, boil, knockout],
    additives: r.lines
      .filter((l) => l.kind === "other")
      .map((l) => ({ name: nameOf(l), amount: l.amountText ?? "" })),
    grist: r.lines
      .filter((l) => l.kind === "fermentable")
      .map((l) => ({ name: nameOf(l), pounds: pounds(l.grams) })),
    hops: sorted.map((l) => ({ name: nameOf(l), pounds: pounds(l.grams), time: hopTime(l) })),
    yeast: r.lines
      .filter((l) => l.kind === "yeast")
      .map((l) => ({ name: nameOf(l), gen: genOf(l) })),
    carbonation: r.carbonationVols === null ? "" : Number(r.carbonationVols).toFixed(1),
    notes: b?.notes ?? "",
  };
}

/**
 * How many rows one of the sheet's tables gets: its lines, a spare row to write one in, and at least as
 * many as the paper has; without the spare, its lines alone, as a long recipe needs the room.
 */
export const tableRows = (lines: number, least: number, spare: boolean) =>
  spare ? Math.max(lines + 1, least) : Math.max(lines, 1);

/** The paper's least rows for each table: what White Oak's sheet gives the ones it fills by hand. */
export const LEAST_ROWS = { additives: 2, grist: 4, hops: 3, yeast: 1 } as const;

/**
 * How the Brew Log fits one page: nine blank rows for the cellar's gravity readings, as the paper has
 * room for, fewer as a long recipe's lines take the room, and set closer, with no spare rows, when even
 * three would not fit. Measured in CSS pixels on a Letter page (A4 is longer), with room to spare.
 */
export function sheetSize(log: BrewLog): { compact: boolean; cellarRows: number } {
  // What the title, the dates, the notes and the foot leave the day's blanks, the tables and the cellar.
  const room = 680;
  const fields = log.fields.reduce((n, run) => n + run.length, 0);
  const runs = log.fields.length;
  const rowsLeft = (compact: boolean) => {
    const row = compact ? 18 : 23;
    const table = (lines: number, least: number, spare = !compact) =>
      row * (1 + tableRows(lines, least, spare)) + 10;
    const left = fields * (compact ? 17 : 21.5) + (runs - 1) * 8;
    const right =
      30 +
      table(log.additives.length, LEAST_ROWS.additives) +
      table(log.grist.length, LEAST_ROWS.grist) +
      table(log.hops.length, LEAST_ROWS.hops) +
      table(log.yeast.length, LEAST_ROWS.yeast, false);
    return Math.floor((room - Math.max(left, right) - row - 10) / row);
  };
  const rows = rowsLeft(false);
  if (rows >= 3) return { compact: false, cellarRows: Math.min(rows, 9) };
  return { compact: true, cellarRows: Math.min(Math.max(rowsLeft(true), 3), 9) };
}
